import * as angular from 'angular';
import * as template from './template.html';
import './styling.scss';
import { isDp3Policy, isHomePolicy } from 'common/source/utilities/policyInfoHelper';
import { getLongNumericAmericanDateString } from 'common/source/utilities/dates';

const module = angular.module('overview-my-documents-section', []);

interface CoverageDocument {
    icon: string;
    name: string;
    url: string;
}

interface DocumentType {
    name: string;
    documents: Array<CoverageDocument>;
    expandByDefault: boolean;
}

interface OverviewMyDocumentsSectionController extends angular.IStrictController {
    title: string;
    icon: string;
    description: string;
    policyOverviewDocuments: Array<CoverageDocument>;
    policyChangesDocuments: Array<CoverageDocument>;
    policyNoticesDocuments: Array<CoverageDocument>;
    policyRenewalDocuments: Array<CoverageDocument>;
    documentsProvidedByCustomer: Array<CoverageDocument>;
    documentTypes: Array<DocumentType>;
    policyInfo: CustomerPolicyInfo | LenderPolicyInfo;
}

const HOME_POLICY_DOCUMENT_URL =
    'https://s3-us-west-2.amazonaws.com/hippo-document-prod/policy-overview/Hippo+HO3+Booklet.pdf';
const CONDO_POLICY_DOCUMENT_URL =
    'https://s3-us-west-2.amazonaws.com/hippo-document-prod/policy-overview/Hippo+HO6+Booklet.pdf';

const overviewIcon = 'documents-with-house';
const noticesIcon = 'documents-with-bell';
const changesIcon = 'documents-with-gear';
const renewalIcon = 'documents-with-renewal';

const getValuationDoc = (documents: AggregateDocuments) => {
    if (Array.isArray(documents.valuation)) {
        if (documents.valuation.length) {
            return documents.valuation[documents.valuation.length - 1];
        }
    } else {
        return documents.valuation;
    }
};

module.component('overviewMyDocumentsSection', {
    template: template,
    bindings: {
        policyInfo: '<'
    },
    // tslint:disable-next-line:no-big-function
    controller: function(this: OverviewMyDocumentsSectionController) {
        'ngInject';

        // tslint:disable-next-line: no-big-function
        this.$onInit = () => {
            this.title = 'My Documents';
            this.icon = 'documents';
            this.description =
                'Insurance document center. Download and review your policy information, evidence of insurance and more.';

            const isHome = isHomePolicy(this.policyInfo);
            const documents: AggregateDocuments = this.policyInfo.aggregate_documents;

            this.policyOverviewDocuments = [];

            if (documents.eoi) {
                this.policyOverviewDocuments.push({
                    icon: overviewIcon,
                    name: 'Evidence of Insurance (EOI)',
                    url: documents.eoi.url
                });
            }

            const valuationDoc = getValuationDoc(documents);
            if (valuationDoc) {
                this.policyOverviewDocuments.push({
                    icon: overviewIcon,
                    name: 'Home Rebuilding Cost Estimate (RCE)',
                    url: valuationDoc.url
                });
            }

            if (documents.declaration) {
                this.policyOverviewDocuments.push({
                    icon: overviewIcon,
                    name: 'Declarations Page',
                    url: documents.declaration.url
                });
            }

            if (documents.policy) {
                this.policyOverviewDocuments.push({
                    icon: overviewIcon,
                    name: 'Full Policy Application',
                    url: documents.policy.url
                });
            }

            this.policyOverviewDocuments.push({
                icon: overviewIcon,
                name: 'Terms of Use',
                url: 'https://s3-us-west-2.amazonaws.com/hippo-document-prod/legal+/HIPPO-TermsofUse.pdf'
            });

            if (!isDp3Policy(this.policyInfo)) {
                this.policyOverviewDocuments.push({
                    icon: overviewIcon,
                    name: 'Policy Summary',
                    url: isHome ? HOME_POLICY_DOCUMENT_URL : CONDO_POLICY_DOCUMENT_URL
                });
            }

            this.policyChangesDocuments = [];

            if (documents.endorsement) {
                documents.endorsement.forEach((document) => {
                    this.policyChangesDocuments.push({
                        icon: changesIcon,
                        name: `Endorsement doc ${getLongNumericAmericanDateString(document.created_at)}`,
                        url: document.url
                    });
                });
            }

            this.policyNoticesDocuments = [];

            if (documents.cancellation_notice) {
                documents.cancellation_notice.forEach((document) => {
                    this.policyNoticesDocuments.push({
                        icon: noticesIcon,
                        name: `Cancellation Notice ${getLongNumericAmericanDateString(document.created_at)}`,
                        url: document.url
                    });
                });
            }

            if (documents.revert_cancel) {
                documents.revert_cancel.forEach((document) => {
                    this.policyNoticesDocuments.push({
                        icon: noticesIcon,
                        name: `Reinstatement Notice ${getLongNumericAmericanDateString(document.created_at)}`,
                        url: document.url
                    });
                });
            }

            if (documents.expiration_notice) {
                this.policyNoticesDocuments.push({
                    icon: noticesIcon,
                    name: `Expiration Notice ${getLongNumericAmericanDateString(
                        documents.expiration_notice.created_at
                    )}`,
                    url: documents.expiration_notice.url
                });
            }

            this.policyRenewalDocuments = [];

            if (documents.next_renewal_offer) {
                this.policyRenewalDocuments.push({
                    icon: renewalIcon,
                    name: `Renewal Offer ${getLongNumericAmericanDateString(documents.next_renewal_offer.created_at)}`,
                    url: documents.next_renewal_offer.url
                });
            }

            if (documents.renewal_eoi) {
                this.policyRenewalDocuments.push({
                    icon: renewalIcon,
                    name: `Renewal Evidence of Insurance ${getLongNumericAmericanDateString(
                        documents.renewal_eoi.created_at
                    )}`,
                    url: documents.renewal_eoi.url
                });
            }

            this.documentsProvidedByCustomer = [
                // {
                //     icon: 'documents-with-person',
                //     name: 'Roof_fixing_estimate.pdf [dd/mm/yyy]',
                //     url: 'https://www.google.com'
                // }
            ];

            this.documentTypes = [
                {
                    name: 'Policy Overview',
                    documents: this.policyOverviewDocuments,
                    expandByDefault: true
                },
                {
                    name: 'Policy Changes (Endorsements)',
                    documents: this.policyChangesDocuments,
                    expandByDefault: false
                },
                {
                    name: 'Policy Notices',
                    documents: this.policyNoticesDocuments,
                    expandByDefault: false
                },
                {
                    name: 'Renewal Documents',
                    documents: this.policyRenewalDocuments,
                    expandByDefault: false
                },
                {
                    name: 'Documents Provided by You',
                    documents: this.documentsProvidedByCustomer,
                    expandByDefault: false
                }
            ];
        };
    }
});

export { module };
